import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { CHART_COLORS, getAgChartTheme, getChartColors, formatNumber, formatCurrency } from './chartTheme';
import { useTheme } from '../../../stores/ThemeContext';
import { parseISO, differenceInMinutes } from 'date-fns';
/**
 * @typedef {Object} Trade
 * @property {number} profit - Trade profit/loss
 * @property {string} [openTime] - Open time (ISO string)
 * @property {string} [closeTime] - Close time (ISO string)
 * @property {string} [symbol] - Trading symbol
 */

/**
 * @typedef {Object} ScatterDataPoint
 * @property {number} holdingTime - Holding time in minutes
 * @property {string} holdingTimeLabel - Formatted holding time
 * @property {number} profit - Trade profit
 * @property {string} result - 'Win' or 'Loss'
 * @property {string} fill - Point color
 * @property {string} [symbol] - Trading symbol
 */

/**
 * Breakeven threshold for categorizing trades
 */
const BREAKEVEN_THRESHOLD = 0.01;

/**
 * Format holding time to human-readable string
 * @param {number} minutes - Holding time in minutes
 * @returns {string} - Formatted string (e.g., "2h 30m", "45m", "1d 4h")
 */
const formatHoldingTime = (minutes) => {
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

/**
 * Holding Time vs Profit Scatter Plot
 * Visualizes relationship between trade duration and profitability
 *
 * @param {Object} props
 * @param {Trade[]} props.trades - Array of trade objects with openTime, closeTime, profit
 * @param {string} [props.title='Holding Time vs Profit'] - Chart title
 * @param {number} [props.height=350] - Chart height in pixels
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {number} [props.maxHoldingMinutes] - Max holding time to display (filters outliers)
 *
 * @example
 * <HoldingTimeProfitScatter
 *   trades={trades}
 *   title="Trade Duration Analysis"
 *   height={400}
 * />
 *
 * @example
 * // Filter to show only trades under 8 hours
 * <HoldingTimeProfitScatter
 *   trades={trades}
 *   maxHoldingMinutes={480}
 * />
 */
const HoldingTimeProfitScatter = ({
  trades = [],
  title = 'Holding Time vs Profit',
  height = 350,
  className = '',
  maxHoldingMinutes,
}) => {
  const { isDark } = useTheme();
  const chartColors = getChartColors(isDark);
  const chartRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height });

  // Handle responsive resizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height });
        }
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [height]);

  // Compute scatter plot data
  const { winData, lossData, stats } = useMemo(() => {
    if (!trades || trades.length === 0) {
      return { winData: [], lossData: [], stats: null };
    }

    /** @type {ScatterDataPoint[]} */
    const wins = [];
    /** @type {ScatterDataPoint[]} */
    const losses = [];

    let totalHoldingTime = 0;
    let validTrades = 0;

    trades.forEach((trade) => {
      // Calculate holding time
      const openTime = trade.openTime;
      const closeTime = trade.closeTime;

      if (!openTime || !closeTime) return;

      let openDate, closeDate;
      try {
        openDate = typeof openTime === 'string' ? parseISO(openTime) : new Date(openTime);
        closeDate = typeof closeTime === 'string' ? parseISO(closeTime) : new Date(closeTime);
        if (isNaN(openDate.getTime()) || isNaN(closeDate.getTime())) return;
      } catch {
        return;
      }

      const holdingTime = differenceInMinutes(closeDate, openDate);

      // Skip negative or zero holding times
      if (holdingTime <= 0) return;

      // Filter by max holding time if specified
      if (maxHoldingMinutes && holdingTime > maxHoldingMinutes) return;

      const profit = Number(trade.profit) || 0;
      const isWin = profit > BREAKEVEN_THRESHOLD;

      const dataPoint = {
        holdingTime,
        holdingTimeLabel: formatHoldingTime(holdingTime),
        profit,
        result: isWin ? 'Win' : 'Loss',
        fill: isWin ? CHART_COLORS.win : CHART_COLORS.loss,
        symbol: trade.symbol || 'Unknown',
      };

      if (isWin) {
        wins.push(dataPoint);
      } else {
        losses.push(dataPoint);
      }

      totalHoldingTime += holdingTime;
      validTrades++;
    });

    // Calculate stats
    const avgHoldingTime = validTrades > 0 ? totalHoldingTime / validTrades : 0;
    const winAvgHolding = wins.length > 0
      ? wins.reduce((sum, d) => sum + d.holdingTime, 0) / wins.length
      : 0;
    const lossAvgHolding = losses.length > 0
      ? losses.reduce((sum, d) => sum + d.holdingTime, 0) / losses.length
      : 0;

    return {
      winData: wins,
      lossData: losses,
      stats: {
        avgHoldingTime,
        winAvgHolding,
        lossAvgHolding,
        totalPoints: validTrades,
      },
    };
  }, [trades, maxHoldingMinutes]);

  // Determine x-axis label based on data range
  const xAxisLabel = useMemo(() => {
    const allData = [...winData, ...lossData];
    if (allData.length === 0) return 'Holding Time (minutes)';

    const maxTime = Math.max(...allData.map(d => d.holdingTime));
    if (maxTime > 1440) return 'Holding Time (minutes)'; // > 1 day
    if (maxTime > 60) return 'Holding Time (minutes)';
    return 'Holding Time (minutes)';
  }, [winData, lossData]);

  // Chart options
  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    title: {
      text: title,
      fontSize: 16,
      fontWeight: 600,
      color: chartColors.text,
    },
    series: [
      // Win scatter points
      {
        type: 'scatter',
        data: winData,
        xKey: 'holdingTime',
        yKey: 'profit',
        title: 'Wins',
        marker: {
          fill: CHART_COLORS.win,
          stroke: CHART_COLORS.win,
          fillOpacity: 0.7,
          strokeOpacity: 1,
          size: 8,
          shape: 'circle',
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: `${datum.result} - ${datum.symbol}`,
            content: `Holding Time: ${datum.holdingTimeLabel}\nProfit: ${formatCurrency(datum.profit)}`,
          }),
        },
        highlightStyle: {
          item: {
            fillOpacity: 1,
            strokeWidth: 2,
            stroke: chartColors.text,
          },
        },
      },
      // Loss scatter points
      {
        type: 'scatter',
        data: lossData,
        xKey: 'holdingTime',
        yKey: 'profit',
        title: 'Losses',
        marker: {
          fill: CHART_COLORS.loss,
          stroke: CHART_COLORS.loss,
          fillOpacity: 0.7,
          strokeOpacity: 1,
          size: 8,
          shape: 'circle',
        },
        tooltip: {
          renderer: ({ datum }) => ({
            title: `${datum.result} - ${datum.symbol}`,
            content: `Holding Time: ${datum.holdingTimeLabel}\nLoss: ${formatCurrency(datum.profit)}`,
          }),
        },
        highlightStyle: {
          item: {
            fillOpacity: 1,
            strokeWidth: 2,
            stroke: chartColors.text,
          },
        },
      },
    ],
    axes: [
      {
        type: 'number',
        position: 'bottom',
        title: {
          text: xAxisLabel,
          color: chartColors.textMuted,
          fontSize: 12,
        },
        label: {
          color: chartColors.textMuted,
          fontSize: 11,
          formatter: ({ value }) => {
            if (value >= 1440) return `${Math.round(value / 1440)}d`;
            if (value >= 60) return `${Math.round(value / 60)}h`;
            return `${Math.round(value)}m`;
          },
        },
        line: {
          stroke: CHART_COLORS.axis,
        },
        gridLine: {
          style: [{
            stroke: CHART_COLORS.gridLine,
            lineDash: [4, 2],
          }],
        },
      },
      {
        type: 'number',
        position: 'left',
        title: {
          text: 'Profit/Loss ($)',
          color: chartColors.textMuted,
          fontSize: 12,
        },
        label: {
          color: chartColors.textMuted,
          fontSize: 11,
          formatter: ({ value }) => {
            if (Math.abs(value) >= 1000) {
              return `$${(value / 1000).toFixed(1)}k`;
            }
            return `$${value.toFixed(0)}`;
          },
        },
        line: {
          stroke: CHART_COLORS.axis,
        },
        gridLine: {
          style: [{
            stroke: CHART_COLORS.gridLine,
            lineDash: [4, 2],
          }],
        },
        // Add zero line
        crossLines: [{
          type: 'line',
          value: 0,
          stroke: CHART_COLORS.neutral,
          strokeWidth: 1,
          lineDash: [4, 4],
        }],
      },
    ],
    legend: {
      enabled: true,
      position: 'bottom',
      item: {
        marker: {
          shape: 'circle',
          size: 10,
        },
        label: {
          color: chartColors.text,
          fontSize: 12,
        },
        paddingX: 16,
        paddingY: 8,
      },
    },
    background: {
      fill: CHART_COLORS.background,
    },
  }), [winData, lossData, title, xAxisLabel, isDark]);

  // Empty state
  if (!trades || trades.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No trade data</p>
          <p className="text-sm">Add trades with open/close times to see duration analysis</p>
        </div>
      </div>
    );
  }

  // No valid data (missing timestamps)
  if (winData.length === 0 && lossData.length === 0) {
    return (
      <div
        ref={containerRef}
        className={`flex items-center justify-center ${className}`}
        style={{ height }}
      >
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No timing data available</p>
          <p className="text-sm">Trades need both open and close times</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`w-full ${className}`} style={{ height }}>
      <AgCharts ref={chartRef} options={options} />
      {stats && (
        <div className="flex justify-center gap-6 mt-2 text-xs text-muted-foreground">
          <span>Avg Hold: {formatHoldingTime(stats.avgHoldingTime)}</span>
          <span className="text-green-500">Win Avg: {formatHoldingTime(stats.winAvgHolding)}</span>
          <span className="text-red-500">Loss Avg: {formatHoldingTime(stats.lossAvgHolding)}</span>
        </div>
      )}
    </div>
  );
};

export default HoldingTimeProfitScatter;
